import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'ds-brand',
  imports: [RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ds-brand' },
  template: `<a [routerLink]="home()" [attr.aria-label]="ariaLabel()"
    ><span class="ds-brand__mark" aria-hidden="true"
      ><img
        src="/assets/brand/iqlabtest-mark.svg"
        alt="IQLabTest logo"
        width="36"
        height="36" /></span
    ><span class="ds-brand__copy"
      ><strong>IQLabTest</strong>
      @if (tagline()) {
        <small>{{ tagline() }}</small>
      }
    </span></a
  >`,
})
export class DsBrandComponent {
  readonly home = input('/');
  readonly tagline = input('');
  readonly ariaLabel = input('IQLabTest home');
}

@Component({
  selector: 'ds-header',
  imports: [DsBrandComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ds-header' },
  template: `<header [class.ds-header--menu-open]="menuOpen()">
    <div class="ds-header__inner">
      <ds-brand [tagline]="tagline()" />
      <button
        class="ds-header__menu-toggle"
        type="button"
        [attr.aria-expanded]="menuOpen()"
        [attr.aria-controls]="menuId()"
        [attr.aria-label]="menuLabel()"
        (click)="menuOpenChange.emit(!menuOpen())"
      >
        <span class="ds-header__menu-icon" aria-hidden="true">
          <span></span><span></span><span></span>
        </span>
      </button>
      <nav [id]="menuId()" [attr.aria-label]="navigationLabel()">
        <ng-content select="[dsHeaderNav]" />
      </nav>
      <div class="ds-header__actions"><ng-content select="[dsHeaderActions]" /></div>
    </div>
  </header>`,
})
export class DsHeaderComponent {
  readonly tagline = input('');
  readonly navigationLabel = input('Primary navigation');
  readonly menuLabel = input('Open navigation menu');
  readonly menuId = input('site-navigation');
  readonly menuOpen = input(false);
  readonly menuOpenChange = output<boolean>();
}

@Component({
  selector: 'ds-footer',
  imports: [DsBrandComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ds-footer' },
  template: `<footer>
    <div class="ds-footer__inner">
      <ds-brand />
      <nav [attr.aria-label]="navigationLabel()"><ng-content select="[dsFooterNav]" /></nav>
      <div class="ds-footer__meta"><ng-content /></div>
    </div>
  </footer>`,
})
export class DsFooterComponent {
  readonly navigationLabel = input('Footer navigation');
}

export interface DsTab {
  id: string;
  label: string;
  disabled?: boolean;
}
@Component({
  selector: 'ds-tabs',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ds-tabs' },
  template: `<div
      class="ds-tabs__list"
      role="tablist"
      [attr.aria-label]="label()"
      (keydown)="onKeydown($event)"
    >
      @for (tab of tabs(); track tab.id; let index = $index) {
        <button
          type="button"
          role="tab"
          [id]="tab.id + '-tab'"
          [attr.aria-controls]="tab.id + '-panel'"
          [attr.aria-selected]="tab.id === selectedId()"
          [tabIndex]="tab.id === selectedId() ? 0 : -1"
          [disabled]="tab.disabled"
          (click)="select(tab.id)"
        >
          {{ tab.label }}
        </button>
      }
    </div>
    <ng-content />`,
})
export class DsTabsComponent {
  readonly tabs = input.required<DsTab[]>();
  readonly selectedId = input.required<string>();
  readonly label = input('Sections');
  readonly selectedIdChange = output<string>();
  select(id: string): void {
    if (!this.tabs().find((tab) => tab.id === id)?.disabled) this.selectedIdChange.emit(id);
  }
  onKeydown(event: KeyboardEvent): void {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const enabled = this.tabs().filter((tab) => !tab.disabled);
    if (!enabled.length) return;
    const current = Math.max(
      0,
      enabled.findIndex((tab) => tab.id === this.selectedId()),
    );
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? enabled.length - 1
          : (current + (event.key === 'ArrowRight' ? 1 : -1) + enabled.length) % enabled.length;
    event.preventDefault();
    this.select(enabled[next].id);
    const tabList = (event.target as HTMLElement).closest('[role="tablist"]');
    const enabledButtons = Array.from(
      tabList?.querySelectorAll<HTMLButtonElement>('[role="tab"]:not(:disabled)') ?? [],
    );
    enabledButtons[next]?.focus();
  }
}
